import React, { useState } from 'react';
import { Star, Loader2 } from 'lucide-react';
import { RATING_TAGS, rateRide } from '../lib/account';

// Stars, quick tags and an optional note for a finished ride. Saved on the server, so the chauffeur sees it (as part of their figures,
// never next to this ride). Sending again changes the rating.
const RatingBox = ({ ride, onRated }) => {
  const existing = ride.rating && typeof ride.rating === 'object' ? ride.rating : null;
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(existing?.stars || 0);
  const [tags, setTags] = useState(existing?.tags || []);
  const [note, setNote] = useState(existing?.note || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const toggleTag = (t) => setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const submit = async () => {
    if (!stars) { setError('Choose 1 to 5 stars.'); return; }
    setBusy(true);
    setError('');
    try {
      const saved = await rateRide(ride.reference || ride.id, { stars, tags, note });
      onRated(ride, saved);
      setOpen(false);
    } catch (e) {
      setError(e.message || 'We could not save your rating.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div data-testid="rating-summary" className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
        <span className="text-[11px] tracking-[0.15em] uppercase text-gray-500">{existing ? 'Your rating' : 'Rate your chauffeur'}</span>
        <button type="button" data-testid="rating-open" onClick={() => setOpen(true)} className="flex items-center gap-1 text-sm text-orange-300 hover:text-orange-200">
          {existing ? (
            <>
              {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= existing.stars ? 'fill-orange-500 text-orange-500' : 'text-gray-600'}`} />)}
              <span className="ml-1 text-xs text-gray-400">Change</span>
            </>
          ) : 'Rate'}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="rating-box" className="mt-3 pt-3 border-t border-white/5 space-y-3">
      <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={stars === n} aria-label={`${n} star${n > 1 ? 's' : ''}`} data-testid={`star-${n}`} onClick={() => setStars(n)}>
            <Star className={`h-7 w-7 transition-colors ${n <= stars ? 'fill-orange-500 text-orange-500' : 'text-gray-600'}`} />
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {RATING_TAGS.map((t) => (
          <button key={t} type="button" data-testid={`tag-${t}`} aria-pressed={tags.includes(t)} onClick={() => toggleTag(t)}
            className={`rounded-full border px-3 py-1 text-xs transition-all ${tags.includes(t) ? 'border-orange-500/60 text-orange-300 bg-orange-500/[0.1]' : 'border-white/10 text-gray-400 hover:border-orange-400/40'}`}>
            {t}
          </button>
        ))}
      </div>
      <textarea data-testid="rating-note" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note (optional)"
        className="w-full rounded-xl bg-white/[0.03] border border-white/10 p-2.5 text-sm text-white placeholder-gray-600 focus:border-orange-500 focus:outline-none" />
      {error && <p data-testid="rating-error" className="text-xs text-red-400">{error}</p>}
      <div className="flex gap-2">
        <button type="button" data-testid="rating-submit" disabled={busy} onClick={submit}
          className="flex-1 rounded-full py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 disabled:opacity-50 flex items-center justify-center">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit rating'}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="rounded-full px-4 text-sm text-gray-400 border border-white/10">Cancel</button>
      </div>
    </div>
  );
};

export default RatingBox;
